import React, { useMemo, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { validateForm } from "utils/validation";
import Spinner from "Common/Globle/Spinner";
import fields from "../json/AssignFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import useFetchAllData from "Hooks/useFetchAllData";
import { assignEmployees, GetAllRosterList, getEmployeesList, getAssignedEmployees } from "adminApi/DutyRosterApi/AssignedEmployeeApi";
import { AssignRules } from "validation/ValidationRules";
import EditAssignedEmployeeForm from "./Componates/EditAssignedEmployeeForm";

const EditAssignedEmployee = () => {
    const { id: urlRosterId } = useParams();
    const navigate = useNavigate();
    const { formFields } = fields;
    const [isRosterSelected, setIsRosterSelected] = useState(false);

    // Build initial values dynamically from fields
    const initialValues = useMemo(() => {
        return formFields.reduce((acc, field) => {
            if (field.name === "employee_ids") {
                acc[field.name] = [];
            } else if (field.name === "roster_id") {
                acc[field.name] = urlRosterId || "";
            } else {
                acc[field.name] = "";
            }
            return acc;
        }, {});
    }, [formFields, urlRosterId]);

    const {
        values,
        errors,
        handleChange,
        setErrors,
        setValues
    } = useFormChange(initialValues, AssignRules);

    // Set initial selection state if ID is in URL
    useEffect(() => {
        if (urlRosterId) {
            setIsRosterSelected(true);
        }
    }, [urlRosterId]);

    // 🔹 Fetch already assigned employees when roster selection changes
    useEffect(() => {
        if (values.roster_id) {
            const fetchAssignedData = async () => {
                const res = await getAssignedEmployees(values.roster_id);
                if (res.success && Array.isArray(res.data)) {
                    const assignedIds = res.data.map(emp => emp.employee_id);
                    setValues(prev => ({ ...prev, employee_ids: assignedIds }));
                    setIsRosterSelected(true);
                }
            };
            fetchAssignedData();
        } else {
            setValues(prev => ({ ...prev, employee_ids: [] }));
            setIsRosterSelected(false);
        }
    }, [values.roster_id, setValues]);

    // Fetch Rosters
    const { data: rosterData, loading: loadingRosters } = useFetchAllData(GetAllRosterList);

    // Fetch Employees
    const { data: employeeData, loading: loadingEmployees } = useFetchAllData(getEmployeesList);

    
    const rosterOptions = useMemo(() => {
        if (!Array.isArray(rosterData)) return [];
        return rosterData.map(r => ({
            value: r.roster_id,
            label: `${r.shift_name} (${r.start_date} to ${r.end_date})`
        }));
    }, [rosterData]);


    const employeeOptions = useMemo(() => {
        if (!Array.isArray(employeeData)) return [];
        return employeeData.map(e => ({
            value: e.employee_id,
            name: e.employee_name,
            position: e.position_name || 'No Position Found',
            assigned_roster_id: e.assigned_roster_id
        }));
    }, [employeeData]);

    const {
        loading: loadingSubmit,
        handleSubmit
    } = useFormSubmit(
        async (formValues) => {
            return await assignEmployees(formValues);
        },
        (vals) => {
            const err = validateForm(vals, AssignRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Roster assignments updated successfully"
    );

    return (
        <PageBody
            card={true}
            headerchildren={
                <div className="d-flex align-items-center justify-content-between w-100">
                    <HeadingOne>Edit Roster Assignment</HeadingOne>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
                        <i className="fa fa-arrow-left me-1"></i> Back
                    </button>
                </div>
            }
            onSubmit={(e) => handleSubmit(e, values)}
        >
            {(loadingSubmit || loadingRosters || loadingEmployees) && <Spinner isSpinner={true} />}

            <EditAssignedEmployeeForm
                formFields={formFields}
                values={values}
                errors={errors}
                handleChange={handleChange}
                setValues={setValues}
                rosterOptions={rosterOptions}
                employeeOptions={employeeOptions}
                isRosterSelected={isRosterSelected}
                loadingSubmit={loadingSubmit}
            />
        </PageBody>
    );
};

export default EditAssignedEmployee;



